import * as React from "react";

// Enhanced breakpoint system with mobile-first approach
const BREAKPOINTS = {
  xs: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
} as const;

type Breakpoint = keyof typeof BREAKPOINTS;

export function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState<boolean>(() => {
    // Initialize with proper SSR-safe value
    if (typeof window === "undefined") return false;
    return window.innerWidth < BREAKPOINTS.md;
  });

  React.useEffect(() => {
    const updateIsMobile = () => {
      const mobile = window.innerWidth < BREAKPOINTS.md;
      setIsMobile(mobile);
      console.log("Mobile detection update:", {
        width: window.innerWidth,
        isMobile: mobile,
      });
    };

    // Update immediately
    updateIsMobile();

    // Create media query listener for better performance
    const mediaQuery = window.matchMedia(
      `(max-width: ${BREAKPOINTS.md - 1}px)`,
    );

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", updateIsMobile);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(updateIsMobile);
    }

    // Cleanup function
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", updateIsMobile);
      } else {
        mediaQuery.removeListener(updateIsMobile);
      }
    };
  }, []);

  return isMobile;
}

export function useBreakpoint() {
  const [breakpoint, setBreakpoint] = React.useState<Breakpoint>(() => {
    if (typeof window === "undefined") return "md";

    const width = window.innerWidth;
    if (width >= BREAKPOINTS["2xl"]) return "2xl";
    if (width >= BREAKPOINTS.xl) return "xl";
    if (width >= BREAKPOINTS.lg) return "lg";
    if (width >= BREAKPOINTS.md) return "md";
    if (width >= BREAKPOINTS.sm) return "sm";
    return "xs";
  });

  React.useEffect(() => {
    const updateBreakpoint = () => {
      const width = window.innerWidth;
      let newBreakpoint: Breakpoint = "xs";

      if (width >= BREAKPOINTS["2xl"]) newBreakpoint = "2xl";
      else if (width >= BREAKPOINTS.xl) newBreakpoint = "xl";
      else if (width >= BREAKPOINTS.lg) newBreakpoint = "lg";
      else if (width >= BREAKPOINTS.md) newBreakpoint = "md";
      else if (width >= BREAKPOINTS.sm) newBreakpoint = "sm";

      setBreakpoint(newBreakpoint);
    };

    updateBreakpoint();
    window.addEventListener("resize", updateBreakpoint);
    return () => window.removeEventListener("resize", updateBreakpoint);
  }, []);

  return breakpoint;
}

export function useIsTablet() {
  const [isTablet, setIsTablet] = React.useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    const width = window.innerWidth;
    return width >= BREAKPOINTS.md && width < BREAKPOINTS.lg;
  });

  React.useEffect(() => {
    const updateIsTablet = () => {
      const width = window.innerWidth;
      const tablet = width >= BREAKPOINTS.md && width < BREAKPOINTS.lg;
      setIsTablet(tablet);
    };

    updateIsTablet();
    window.addEventListener("resize", updateIsTablet);
    return () => window.removeEventListener("resize", updateIsTablet);
  }, []);

  return isTablet;
}

export function usePrefersReducedMotion() {
  const [prefersReduced, setPrefersReduced] = React.useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });

  React.useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setPrefersReduced(mediaQuery.matches);

    update();
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", update);
    } else {
      mediaQuery.addListener(update);
    }
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", update);
      } else {
        mediaQuery.removeListener(update);
      }
    };
  }, []);

  return prefersReduced;
}
